import { FC, forwardRef, useMemo } from 'react'
import { ReactCalendarGroupRendererProps } from 'react-calendar-timeline'
import { useTranslation } from 'react-i18next'

import { Grid, Link, Typography } from '@mui/material'

import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import languageService from '../../../../../../services/LanguageService'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { LiveEventTagGroup } from '../../../../types/LiveEventTagGroup'
import { TrackingEventTimelineGroup } from '../../../../types/LiveEvents'
import { TrackedGame } from '../../../../types/TrackedGame'
import { EventTagGroupIndicator } from '../../../EventTagGroupIndicator/EventTagGroupIndicator'
import './LiveEventsCalendarTrackingEventGroup.scss'

type LiveEventsCalendarTrackingEventGroupProps = ReactCalendarGroupRendererProps<TrackingEventTimelineGroup> & {
  onClick: (group: TrackingEventTimelineGroup) => void
  onArchetypeClick?: (group: TrackingEventTimelineGroup) => void
  groupByArchetype: boolean
}

export const LiveEventsCalendarTrackingEventGroup: FC<LiveEventsCalendarTrackingEventGroupProps> = forwardRef<
  HTMLDivElement,
  LiveEventsCalendarTrackingEventGroupProps
>(({ group, onClick, onArchetypeClick, groupByArchetype }, ref) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const groupByArchetypeResolvedTitles = useMemo(() => {
    let archetypeName = ''
    let eventTypeName = ''

    if (groupByArchetype) {
      eventTypeName = languageService.getTranslation('tags', group.eventTypeId)
      const resolvedArchetypeName = group.archetypeId
        ? (group.trackedGame as TrackedGame).archetypes.find((archetype) => group.archetypeId === archetype.archetypeId)?.names[userLanguage]
        : undefined
      archetypeName = resolvedArchetypeName ?? '-'
    }

    return {
      archetypeName: archetypeName,
      eventTypeName: eventTypeName,
    }
  }, [groupByArchetype, group, userLanguage])

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      ref={ref}
      style={{ position: 'relative' }}
      id={(group.trackedGame.game.id + '-' + group.id) as string}
      className="LiveEventsCalendarTrackingEventGroup"
      sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
    >
      <EventTagGroupIndicator colorHex={group.colorHex} sx={{ left: '-8px' }} />

      {groupByArchetype && (
        <Grid container direction="row" alignItems="stretch" sx={{ height: '100%', minWidth: 0, flexShrink: 1, flexGrow: 1, width: '100%', maxWidth: '100%' }}>
          <Grid
            item
            xs={6}
            paddingRight={2}
            className="LiveEventsCalendarTrackingEventGroup__EventTypeName"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', minWidth: 0, flexShrink: 1, maxWidth: '50%' }}
          >
            {group.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES ? (
              <GRTooltip content={groupByArchetypeResolvedTitles.eventTypeName}>
                <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', minWidth: 0, fontWeight: 'bold' }}>
                  {groupByArchetypeResolvedTitles.eventTypeName}
                </Typography>
              </GRTooltip>
            ) : (
              <GRTooltip content={groupByArchetypeResolvedTitles.eventTypeName}>
                <Link
                  underline="none"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
                    display: 'block',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => onClick(group as TrackingEventTimelineGroup)}
                >
                  {groupByArchetypeResolvedTitles.eventTypeName}
                </Link>
              </GRTooltip>
            )}
          </Grid>
          <Grid
            item
            xs={6}
            textAlign="right"
            paddingLeft={0.5}
            className="LiveEventsCalendarTrackingEventGroup__ArchetypeName"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minWidth: 0, flexShrink: 1, maxWidth: '50%' }}
          >
            <GRTooltip content={group.archetypeId ? groupByArchetypeResolvedTitles.archetypeName : t('live-events:no_archetype')}>
              {group.archetypeId ? (
                <Link
                  underline="none"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => onArchetypeClick?.(group as TrackingEventTimelineGroup)}
                >
                  <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {groupByArchetypeResolvedTitles.archetypeName}
                  </Typography>
                </Link>
              ) : (
                <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', minWidth: 0 }}>
                  {groupByArchetypeResolvedTitles.archetypeName}
                </Typography>
              )}
            </GRTooltip>
          </Grid>
        </Grid>
      )}

      {!groupByArchetype && group.tagGroupId !== LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Link
          underline="none"
          sx={{
            color: (theme) => theme.palette.text.primary,
            '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            minWidth: 0,
            display: 'block',
          }}
          onClick={() => onClick(group as TrackingEventTimelineGroup)}
        >
          {group.title}
        </Link>
      )}

      {!groupByArchetype && group.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', minWidth: 0, fontWeight: 'bold' }}>
          {group.title}
        </Typography>
      )}
    </Grid>
  )
})
